import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { SiteContentContext } from "../context/SiteContentContext";
import { UserContext } from "../context/UserContext";

/**
 * Renders a site content value by key.
 *
 * For regular users: renders the translated text as a plain React fragment
 * (no extra DOM node — safe inside flex containers, headings, etc.).
 *
 * For admins with the content key overlay active: wraps the text in a <span>
 * with a dotted amber underline and a tooltip showing the content key.
 * Clicking the span navigates to the admin site content panel pre-filtered
 * to that key.
 *
 * Use this component for rendered text. For attribute values (placeholder,
 * aria-label, etc.) keep using getContent() directly.
 *
 * @param {object} replace - Optional token replacements e.g. { "{count}": 42 }
 */
export default function ContentText({ contentKey, fallback = "", className, replace }) {
  const { getContent, getContentColor, overlayActive } = useContext(SiteContentContext);
  const { hasRole } = useContext(UserContext);
  const navigate = useNavigate();

  let value = getContent(contentKey, fallback);
  if (replace) {
    for (const [token, replacement] of Object.entries(replace)) {
      value = value.replace(token, String(replacement));
    }
  }

  const color = getContentColor?.(contentKey) || null;
  const style = color ? { color } : undefined;

  if (hasRole("admin") && overlayActive) {
    function handleClick(e) {
      e.preventDefault();
      e.stopPropagation();
      navigate(`/admin/site-content#content-key-${encodeURIComponent(contentKey)}`);
    }

    // A <span> with a button role, not a real <button>: this overlay wraps arbitrary site
    // copy, some of which already sits inside a real button or link (e.g. a CTA's label) —
    // a nested <button> is invalid HTML there and silently breaks the outer control's click.
    // The role plus the click/key handlers below keep it just as operable for admins.
    return (
      // biome-ignore lint/a11y/useSemanticElements: a real <button> is what breaks here — see above
      <span
        role="button"
        tabIndex={0}
        title={contentKey}
        style={style}
        className={`border-b border-dashed border-amber-400 cursor-help bg-transparent p-0 ${className ?? ""}`}
        onClick={handleClick}
        onKeyUp={(e) => e.key === "Enter" && handleClick(e)}
        aria-label={`Edit content key: ${contentKey}`}
      >
        {value}
      </span>
    );
  }

  // Only override styling when an admin actually set a color. With no color, return the bare
  // string exactly as before so the text keeps whatever color the frontend already gives it
  // (inherited / className on the parent) — no wrapping span, no behavior change.
  if (color) {
    return (
      <span style={style} className={className}>
        {value}
      </span>
    );
  }

  return value;
}
